import type { BlueprintInput, SectionInput } from '@/types'
import { isRecord } from '@/utils/typeGuards'

type BlueprintTranslationCacheEntry = {
  id: string
  lang: string
  label?: string
}

export type BlueprintSectionCacheEntry = SectionInput & {
  translations?: {
    items: BlueprintTranslationCacheEntry[]
  }
}

export type BlueprintCacheEntry = BlueprintInput & {
  sections: {
    items: BlueprintSectionCacheEntry[]
  }
}

const getBlueprintSectionItems = (
  data: unknown,
): BlueprintSectionCacheEntry[] | null => {
  if (!isRecord(data) || !isRecord(data.sections)) return null

  return Array.isArray(data.sections.items)
    ? (data.sections.items as unknown[]).filter(
        (item): item is BlueprintSectionCacheEntry =>
          isRecord(item) && typeof item.id === 'string',
      )
    : null
}

export const createOptimisticBlueprintCacheEntry = (
  input: BlueprintInput,
  previous: unknown,
): BlueprintCacheEntry => {
  const previousSectionItems = getBlueprintSectionItems(previous) ?? []

  return {
    ...(isRecord(previous) ? previous : {}),
    ...input,
    sections: {
      ...(isRecord(previous) && isRecord(previous.sections)
        ? previous.sections
        : {}),
      items: previousSectionItems,
    },
  } as BlueprintCacheEntry
}

export const updateBlueprintCacheSections = <T>(
  data: T | undefined,
  updater: (
    sections: BlueprintSectionCacheEntry[],
  ) => BlueprintSectionCacheEntry[],
): T | undefined => {
  if (!isRecord(data)) return data

  const currentSections = getBlueprintSectionItems(data)
  if (!currentSections) return data

  return {
    ...data,
    sections: {
      ...(isRecord(data.sections) ? data.sections : {}),
      items: updater(currentSections),
    },
  } as T
}
